import React, { useMemo, useState, useEffect } from 'react';
import { useRoles } from '@/hooks/useRoles';
import { computeParentChain } from '@/utils/personnelHierarchy';
import { getFormenRegionParks } from '@/utils/formenParks';
import { isSystemAdminPersonnel } from '@/utils/roleUtils';
import axios from 'axios';
import { Plus, TreePine, Home, Calendar, Clock, X, BarChart3, Package, Users, MapPin, Briefcase, CheckCircle, XCircle, Edit, Save, ClipboardList, AlertTriangle, TrendingUp, ChevronDown, UserCheck, Lock, Ban, KeyRound, Copy, Sparkles } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import SupervisorPicker from './SupervisorPicker';
import TeamPicker from './TeamPicker';
import { DraggableModal } from '../ui/draggable-modal';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { DatePicker } from '../ui/DatePicker';
import { DateRangePicker } from '../ui/DateRangePicker';
import DebouncedInput from '../ui/debounced-input';
import { Button } from '../ui/button';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Progress } from '../ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BACKEND_URL } from '../../config';
import { notifyLoginListMaybeChanged } from '../../loginListBroadcast';
import { buildApiCandidates, shouldFailoverBackendRequest } from '../../backendBases';
import { formatTrCepDisplay, normalizeTrCepToLocal11 } from '../../utils/phoneFormat';
import { normalizeSearchText } from '../../utils/stringUtils';
import { canonicalizeReasonType, computeSocialUsedDays } from '../../utils/leaveUtils';
import ImageLightbox from '@/components/ImageLightbox';

const API = BACKEND_URL;
const GOLGE_BASKAN_ID = "golge_admin_baskan";

/**
 * Personel ekleme ve düzenleme formu - Sekmeli yapı
 * Genel bilgiler, Özet, İzinler, Malzemeler, Parklar
 */
const PersonnelForm = ({
  isOpen,
  onClose,
  isEditing,
  isFormReadOnly = false,
  formData,
  setFormData,
  onSubmit,
  loading,
  personnel,
  regions,
  parks,
  setIsQuickRegionDialogOpen,
  setIsQuickParkDialogOpen,
  // Yeni props - görüntüleme için
  leaveRequests = [],
  materialRequests = [],
  getRoleBadge,
  initialTab = "general", // Başlangıç sekmesi
  taskFilters = {}, // Görev takipten aktarılan filtreler
  userRole = "",
  currentPersonnelId = ""
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [setPasswordLoading, setSetPasswordLoading] = useState(false);
  const [issueTempOpen, setIssueTempOpen] = useState(false);
  const [issuedTemp, setIssuedTemp] = useState(null);
  const [issueTempLoading, setIssueTempLoading] = useState(false);
  const [loginBlockedLoading, setLoginBlockedLoading] = useState(false);
  const [loginBlockDialogOpen, setLoginBlockDialogOpen] = useState(false);
  const [loginBlockPendingAction, setLoginBlockPendingAction] = useState(null); // 'block' | 'unblock'
  // İzinler sekmesinde açık olan rapor görüntüleyicinin talep id'si (yoksa null).
  // Tek state; aynı anda yalnız bir rapor büyütülür.
  const [reportViewerLeaveId, setReportViewerLeaveId] = useState(null);
  const roleLower = (userRole || "").toLowerCase();
  const personnelIdForEdit = formData?.id; // Düzenlenen personelin id'si formData'da
  const targetIsSuperAdminRole = (formData?.role || "").toLowerCase() === "admin";
  const isSelfEdit = String(currentPersonnelId || "") === String(personnelIdForEdit);
  const canSetPassword = Boolean(
    isEditing &&
      personnelIdForEdit &&
      (isSelfEdit ||
        (roleLower === "mudur" && !targetIsSuperAdminRole) ||
        (roleLower === "admin" && !targetIsSuperAdminRole) ||
        (roleLower === "golge_admin" && currentPersonnelId === GOLGE_BASKAN_ID && !targetIsSuperAdminRole))
  );
  const canManageLoginBlock =
    roleLower === "admin" && isEditing && personnelIdForEdit && !isSelfEdit;
  const canIssueTemporaryLogin =
    Boolean(
      isEditing &&
        personnelIdForEdit &&
        !isSelfEdit &&
        (roleLower === "mudur" || roleLower === "admin" || (roleLower === "golge_admin" && currentPersonnelId === GOLGE_BASKAN_ID)) &&
        !targetIsSuperAdminRole
    );
  const canEditPhone = roleLower === "mudur" || roleLower === "admin";
  const [taskHistory, setTaskHistory] = useState(null);
  const [taskHistoryLoading, setTaskHistoryLoading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState({ open: false, urls: [] });
  const [selectedDayDetail, setSelectedDayDetail] = useState(null); // Günlük detay için
  
  // Görev filtre state'leri - kullanıcı input'ları
  const [taskFilterStartDate, setTaskFilterStartDate] = useState('');
  const [taskFilterEndDate, setTaskFilterEndDate] = useState('');
  const [taskFilterParkId, setTaskFilterParkId] = useState('all');
  const [regionSearchQuery, setRegionSearchQuery] = useState('');
  const [parkSearchQuery, setParkSearchQuery] = useState('');
  
  // Uygulanan filtre state'leri - sadece "Ara" butonuna basıldığında güncellenir
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [appliedParkId, setAppliedParkId] = useState('all');
  
  const [expandedDates, setExpandedDates] = useState([]); // Akordeon için açık tarihler

  // Dinamik rol/yetki sistemi: rol listesi ve helper'lar
  // Rol referans listesi yalnız diyalog açıkken çekilir — kapalıyken (ör. park
  // görevlisi panelinde her zaman mount olan bu diyalog) `/admin/roles` çağrısı
  // yapılmaz; aksi halde yetkisiz rolde 403 + console hatası oluşur (Kapı 11).
  const { roles, hierarchyRoles, getRoleById, getRoleBySlug, getRoleLabel } = useRoles({ onlyActive: true, enabled: isOpen });

  // Özel Rol Ata dialog'u — yalnızca formData'yı günceller, API çağrısı yapmaz
  const [customRoleDialogOpen, setCustomRoleDialogOpen] = useState(false);
  const [customRoleDraft, setCustomRoleDraft] = useState({ name: '', description: '' });

  // initialTab değiştiğinde activeTab'ı güncelle
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);
  
  // taskFilters değiştiğinde filtre state'lerini güncelle
  useEffect(() => {
    if (taskFilters?.startDate) {
      setTaskFilterStartDate(taskFilters.startDate);
      setTaskFilterEndDate(taskFilters.endDate || taskFilters.startDate);
      setTaskFilterParkId(taskFilters.parkId || 'all');
      // Uygulanan filtreleri de güncelle
      setAppliedStartDate(taskFilters.startDate);
      setAppliedEndDate(taskFilters.endDate || taskFilters.startDate);
      setAppliedParkId(taskFilters.parkId || 'all');
      // İlk tarihi açık bırak
      if (taskFilters.startDate) {
        setExpandedDates([taskFilters.startDate]);
      }
    } else {
      // Varsayılan: bugün
      const today = new Date().toISOString().split('T')[0];
      setTaskFilterStartDate(today);
      setTaskFilterEndDate(today);
      setTaskFilterParkId('all');
      setAppliedStartDate(today);
      setAppliedEndDate(today);
      setAppliedParkId('all');
      setExpandedDates([today]);
    }
  }, [taskFilters, isOpen]);

  // Görev geçmişini yükle - tarih parametreleri ile
  const fetchTaskHistory = async (personnelId, startDate = null, endDate = null) => {
    if (!personnelId) return;
    setTaskHistoryLoading(true);
    try {
      // Tarih parametrelerini kullan veya state'den al
      const start = startDate || taskFilterStartDate;
      const end = endDate || taskFilterEndDate;
      
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      
      const url = `${API}/api/personnel/${personnelId}/task-history${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      setTaskHistory(response.data);
    } catch (error) {
      console.error('Görev geçmişi yüklenemedi:', error);
      toast.error('Görev geçmişi yüklenemedi');
    } finally {
      setTaskHistoryLoading(false);
    }
  };

  // Sekme değiştiğinde görev geçmişini yükle
  useEffect(() => {
    if (activeTab === 'tasks' && isEditing && formData.id && !taskHistory) {
      fetchTaskHistory(formData.id);
    }
  }, [activeTab, isEditing, formData.id]);

  // Dialog kapandığında state'i resetle
  useEffect(() => {
    if (!isOpen) {
      setTaskHistory(null);
      setActiveTab("general");
      setRegionSearchQuery('');
      setParkSearchQuery('');
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      name: '',
      custom_role_name: '',
      custom_role_description: '',
      primary_team_id: '',
      secondary_team_id: '',
      phone: '',
      email: '',
      role: '',
      role_id: '',
      status: 'active',
      parent_personnel_id: null,
      assignment_type: 'NORMAL',
      region_ids: [],
      assigned_park_ids: [],
      is_civil_servant: false,
      hire_date: '',
      annual_leave_days: 20,
      used_leave_days: 0,
      work_schedule: {
        morning_start: '08:15',
        morning_end: '11:45',
        afternoon_start: '13:15',
        afternoon_end: '16:45',
        work_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
      }
    });
  };

  // Düzenleme modunda personel verilerini al
  const currentPersonnel = isEditing && formData.id 
    ? personnel.find(p => p.id === formData.id) || formData 
    : formData;

  // İzin ve malzeme geçmişi (düzenleme modunda)
  const personnelLeaves = isEditing ? leaveRequests.filter(lr => lr.personnel_id === formData.id) : [];
  const personnelMaterials = isEditing ? materialRequests.filter(mr => mr.personnel_id === formData.id || mr.requester_id === formData.id) : [];

  // Hiyerarşi zinciri (dinamik): parent_personnel_id'den yukarı doğru takip et
  const parentChain = useMemo(
    () => computeParentChain(currentPersonnel, personnel, { getRoleById, getRoleBySlug }),
    [currentPersonnel, personnel, getRoleById, getRoleBySlug]
  );

  // Sorumlu parklar.
  // Formen için: park görevlisinden farklı olarak parklar, kirli/tutarsız
  // `assigned_personnel_ids` cache'inden değil, formenin sorumlu olduğu
  // bölgelerden (hiyerarşi) türetilir — kart rozetiyle birebir tutarlı.
  const assignedParks = formData.role === 'formen'
    ? getFormenRegionParks(formData, parks, personnel)
    : parks.filter(park => {
        if (formData.assigned_park_ids?.includes(park.id)) return true;
        if (park.assigned_personnel?.includes(formData.id)) return true;
        if (park.assigned_personnel_names?.includes(formData.name)) return true;
        return false;
      });

  // İzin gruplandırma — reason_type slug/varyant olabileceği için kanonikleştir.
  // Aksi halde slug'lı talepler ("sosyal_izin") gösterimden düşer ama üstteki
  // "(N talep)" sayacına dahil olur → "2 talep ama 1 gösteriliyor" tutarsızlığı.
  const groupedLeaves = personnelLeaves.reduce((acc, leave) => {
    const key = canonicalizeReasonType(leave.reason_type);
    if (!acc[key]) acc[key] = [];
    acc[key].push(leave);
    return acc;
  }, {});
  const leaveTypeOrder = ["Yıllık İzin", "Sosyal İzin", "Rapor", "Diğer"];
  // Kanonik türleri sırayla, tanımsız kalan türleri (varsa) sona ekle —
  // hiçbir talep gösterimden düşmesin (sayaç == gösterim garantisi).
  const leaveTypes = [
    ...leaveTypeOrder.filter(type => groupedLeaves[type]),
    ...Object.keys(groupedLeaves).filter(type => !leaveTypeOrder.includes(type)),
  ];

  // Kullanılan sosyal izin — LeaveManagementPanel ("Personel İzin Durumu")
  // ile birebir aynı türetme. Personel sayacı (used_social_leave_days) seed
  // verisinde güncellenmemiş olabildiğinden, onaylı sosyal taleplerden türetilen
  // toplamla max'ı alınır; böylece liste ile detay aynı değeri gösterir.
  const socialUsedDays = computeSocialUsedDays(formData, personnelLeaves);

  // Malzeme gruplandırma
  const groupedMaterials = personnelMaterials.reduce((acc, mat) => {
    if (!acc[mat.material_type]) acc[mat.material_type] = [];
    acc[mat.material_type].push(mat);
    return acc;
  }, {});
  const materialTypes = Object.keys(groupedMaterials).sort();

  // İstatistikler
  const currentYear = new Date().getFullYear();
  const thisYearRaporDays = personnelLeaves
    .filter(l => l.reason_type === "Rapor" && l.status === "approved" && new Date(l.created_at).getFullYear() === currentYear)
    .reduce((sum, l) => {
      const start = new Date(l.start_date);
      const end = new Date(l.end_date);
      return sum + Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    }, 0);
  const thisYearMaterialCount = personnelMaterials.filter(m => new Date(m.created_at).getFullYear() === currentYear).length;
  const totalMaterialsReceived = personnelMaterials.filter(m => m.status === 'delivered').reduce((sum, m) => sum + m.quantity, 0);

  const getLeaveTypeStyle = (type) => {
    switch(type) {
      case "Yıllık İzin": return { emoji: "🏖️", color: "from-blue-50 to-blue-100", border: "border-blue-300" };
      case "Sosyal İzin": return { emoji: "👨‍👩‍👧‍👦", color: "from-purple-50 to-purple-100", border: "border-purple-300" };
      case "Rapor": return { emoji: "🏥", color: "from-red-50 to-red-100", border: "border-red-300" };
      default: return { emoji: "📋", color: "from-gray-50 to-gray-100", border: "border-gray-300" };
    }
  };

  const getMaterialIcon = (type) => {
    const icons = { 'Tırmık': '🔱', 'Yelpaze Tırmık': '🌀', 'Kürek': '⛏️', 'Çalı Süpürge': '🧹', 'Faraş': '🗑️', 'Budama Makası': '✂️', 'Bahçe Hortumu': '💧', 'Eldiven': '🧤', 'Çöp Torbası': '🛍️', 'Diğer': '📦' };
    return icons[type] || '📦';
  };

  // Bölge seçim toggle
  const handleRegionToggle = (regionId) => {
    const currentRegions = formData.region_ids || [];
    if (currentRegions.includes(regionId)) {
      setFormData({ ...formData, region_ids: currentRegions.filter(id => id !== regionId) });
    } else {
      setFormData({ ...formData, region_ids: [...currentRegions, regionId] });
    }
  };

  // Park seçim toggle
  const handleParkToggle = (parkId) => {
    const currentParks = formData.assigned_park_ids || [];
    if (currentParks.includes(parkId)) {
      setFormData({ ...formData, assigned_park_ids: currentParks.filter(id => id !== parkId) });
    } else {
      setFormData({ ...formData, assigned_park_ids: [...currentParks, parkId] });
    }
  };

  const handleClose = () => {
    setActiveTab("general");
    onClose(false);
  };

  // Rol ve filtreleme
  const sortByName = (list) =>
    [...list].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "tr")
    );

  // Dinamik hiyerarşi: seçilen rolün hierarchy_level'ından yüksek olan aktif personeller
  const selectedRoleDef = useMemo(
    () => (formData?.role_id ? getRoleById(formData.role_id) : getRoleBySlug(formData?.role)),
    [formData?.role, formData?.role_id, getRoleById, getRoleBySlug]
  );

  const eligibleSupervisors = useMemo(() => {
    if (!selectedRoleDef) return [];
    const myLevel = Number(selectedRoleDef.hierarchy_level ?? 0);
    return sortByName(
      (personnel || []).filter(p => {
        if (p.id === formData?.id) return false;
        if (p.status && p.status !== 'active') return false;
        if (isSystemAdminPersonnel(p, { getRoleById })) return false;
        const pRole = p.role_id ? getRoleById(p.role_id) : getRoleBySlug(p.role);
        if (!pRole) return false;
        return Number(pRole.hierarchy_level ?? 0) > myLevel;
      })
    );
  }, [personnel, selectedRoleDef, formData?.id, getRoleById, getRoleBySlug]);

  // Hiyerarşi kökü = admin hariç en yüksek seviye
  const maxHierarchyLevel = useMemo(
    () => hierarchyRoles.reduce((m, r) => Math.max(m, Number(r?.hierarchy_level ?? 0)), 0),
    [hierarchyRoles]
  );

  // "no-role" | "root-role" | "ready" — dropdown her zaman görünür, disabled koşullu
  const supervisorMode = useMemo(() => {
    if (!selectedRoleDef) return "no-role";
    if (Number(selectedRoleDef.hierarchy_level ?? 0) >= maxHierarchyLevel) return "root-role";
    return "ready";
  }, [selectedRoleDef, maxHierarchyLevel]);

  const supervisorPlaceholder = {
    "no-role": "Önce rol seçin",
    "root-role": "Bu rol hiyerarşinin kökü — üst amir yok",
    "ready": "Amir seçin",
  }[supervisorMode];

  // Disabled durumlarda dropdown altında görünür uyarı metni — placeholder bazen
  // SelectValue içinde silikleşip kaçırılabiliyor.
  const supervisorHint = {
    "no-role": "⚠️ Bu alan için önce yukarıdaki Rol alanını seçmelisiniz.",
    "root-role": "ℹ️ Seçili rol hiyerarşinin en üstündedir; bağlanacak üst amir yoktur.",
    "ready": null,
  }[supervisorMode];

  const normalizedRegionSearch = normalizeSearchText(regionSearchQuery);
  const normalizedParkSearch = normalizeSearchText(parkSearchQuery);

  const sortedRegions = sortByName(Array.isArray(regions) ? regions : []);
  const filteredRegions = normalizedRegionSearch
    ? sortedRegions.filter(region =>
        normalizeSearchText(region?.name).includes(normalizedRegionSearch)
      )
    : sortedRegions;

  // Seçili bölgeye göre parkları filtrele, ardından alfabetik sırala ve arama uygula
  const regionScopedParks =
    formData.region_ids && formData.region_ids.length > 0
      ? parks.filter(p => formData.region_ids.includes(p.region_id))
      : parks;
  const sortedRegionScopedParks = sortByName(Array.isArray(regionScopedParks) ? regionScopedParks : []);
  const filteredParks = normalizedParkSearch
    ? sortedRegionScopedParks.filter(park =>
        normalizeSearchText(park?.name).includes(normalizedParkSearch)
      )
    : sortedRegionScopedParks;

  const handleSetPassword = async () => {
    if (!personnelIdForEdit || !newPassword.trim() || newPassword.length < 4) return;
    if (newPassword !== newPasswordConfirm) {
      toast.error("Şifre alanları eşleşmiyor.");
      return;
    }
    setSetPasswordLoading(true);
    try {
      await axios.put(`${API}/api/personnel/${personnelIdForEdit}/set-password`, { new_password: newPassword });
      setSetPasswordOpen(false);
      setNewPassword("");
      setNewPasswordConfirm("");
      toast.success("Şifre güncellendi.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (Array.isArray(d) ? d.join(" ") : "Şifre güncellenemedi"));
    } finally {
      setSetPasswordLoading(false);
    }
  };

  const handleIssueTemporaryLogin = async () => {
    if (!personnelIdForEdit) return;
    setIssueTempLoading(true);
    setIssuedTemp(null);
    try {
      let lastErr = null;
      let res = null;
      for (const base of buildApiCandidates(API)) {
        const url = base
          ? `${base}/api/personnel/${personnelIdForEdit}/issue-temporary-login`
          : `/api/personnel/${personnelIdForEdit}/issue-temporary-login`;
        try {
          res = await axios.post(url, {}, { timeout: 20000 });
          break;
        } catch (err) {
          lastErr = err;
          if (shouldFailoverBackendRequest(err)) continue;
          const d = err.response?.data?.detail;
          toast.error(typeof d === "string" ? d : "Kod üretilemedi.");
          return;
        }
      }
      if (!res) {
        const d = lastErr?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Sunucuya bağlanılamadı.");
        return;
      }
      const data = res.data || {};
      setIssuedTemp({
        code: data.code,
        expires_at: data.expires_at,
        message: data.message,
      });
      setIssueTempOpen(true);
      notifyLoginListMaybeChanged();
    } catch (e) {
      toast.error(e.message || "Kod üretilemedi.");
    } finally {
      setIssueTempLoading(false);
    }
  };

  const openLoginBlockConfirm = () => {
    if (!personnelIdForEdit || !canManageLoginBlock) return;
    setLoginBlockPendingAction(formData.login_blocked ? "unblock" : "block");
    setLoginBlockDialogOpen(true);
  };

  const executeLoginBlockChange = async () => {
    if (!personnelIdForEdit || !loginBlockPendingAction) return;
    const next = loginBlockPendingAction === "block";
    setLoginBlockedLoading(true);
    try {
      await axios.put(`${API}/api/personnel/${personnelIdForEdit}/login-blocked`, { blocked: next });
      setFormData((prev) => ({ ...prev, login_blocked: next }));
      notifyLoginListMaybeChanged();
      toast.success(next ? "Giriş engellendi." : "Giriş engeli kaldırıldı.");
      setLoginBlockDialogOpen(false);
      setLoginBlockPendingAction(null);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "İşlem yapılamadı.");
    } finally {
      setLoginBlockedLoading(false);
    }
  };

  return (
    <>
    <DraggableModal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-3">
          {isEditing && formData.name && (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
              {formData.name?.charAt(0)}
            </div>
          )}
          <div>
            <span className="text-lg">
              {isEditing ? formData.name || 'Personel Düzenle' : 'Yeni Personel Ekle'}
            </span>
            {isFormReadOnly && (
              <span className="ml-2 text-sm font-normal text-amber-600 bg-amber-50 px-2 py-1 rounded">
                (Sadece Görüntüleme)
              </span>
            )}
          </div>
        </div>
      }
      subtitle={
        isFormReadOnly ? "Bu personeli görüntülüyorsunuz. Değişiklik yapma yetkiniz bulunmamaktadır." : 
        (isEditing && getRoleBadge ? (
          <Badge className={`text-xs ${getRoleBadge(formData.role).className}`}>
            {getRoleBadge(formData.role).label}
          </Badge>
        ) : "Personel bilgilerini girin")
      }
      maxWidth="max-w-5xl"
    >
        <div className="max-h-[70vh] overflow-y-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={`grid w-full mb-4 ${isEditing ? 'grid-cols-7' : 'grid-cols-1'}`}>
              <TabsTrigger value="general" className="flex items-center gap-1">
                <Edit className="h-4 w-4" />
                <span>Genel</span>
              </TabsTrigger>
              {isEditing && (
                <>
                  <TabsTrigger value="overview" className="flex items-center gap-1">
                    <BarChart3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Özet</span>
                  </TabsTrigger>
                  <TabsTrigger value="tasks" className="flex items-center gap-1">
                    <ClipboardList className="h-4 w-4" />
                    <span className="hidden sm:inline">Görevler</span>
                  </TabsTrigger>
                  <TabsTrigger value="leaves" className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span className="hidden sm:inline">İzinler</span>
                    {personnelLeaves.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{personnelLeaves.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="materials" className="flex items-center gap-1">
                    <Package className="h-4 w-4" />
                    <span className="hidden sm:inline">Malzemeler</span>
                    {personnelMaterials.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{personnelMaterials.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="parks" className="flex items-center gap-1">
                    <TreePine className="h-4 w-4" />
                    <span className="hidden sm:inline">Parklar</span>
                    {assignedParks.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{assignedParks.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="substitutions" className="flex items-center gap-1 text-indigo-600">
                    <Users className="h-4 w-4" />
                    <span className="hidden sm:inline">Vekalet</span>
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            {/* GENEL SEKME - Düzenleme Formu */}
            <TabsContent value="general">
              <form onSubmit={(e) => {
                e.preventDefault();
                if (isFormReadOnly) {
                  toast.error("Değişiklik yapma yetkiniz bulunmamaktadır");
                  return;
                }
                onSubmit(e);
              }} className="space-y-4">
                {isFormReadOnly && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-amber-700 text-sm font-medium">
                      ⚠️ Bu formu sadece görüntüleme modunda açtınız. Değişiklik yapamazsınız.
                    </p>
                  </div>
                )}
                {/* Temel Bilgiler */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="personnel-name">Ad Soyad *</Label>
                    <DebouncedInput
                      id="personnel-name"
                      value={formData.name}
                      onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
                      required
                      disabled={isFormReadOnly}
                      className={isFormReadOnly ? "bg-gray-100" : ""}
                    />
                  </div>
                    {(formData.role === 'bolge_muhendisi' || formData.role === 'mudur_yardimcisi') && (
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          id="personnel-civil-servant"
                          type="checkbox"
                          checked={Boolean(formData.is_civil_servant)}
                          onChange={(e) => setFormData({ ...formData, is_civil_servant: e.target.checked })}
                          disabled={isFormReadOnly}
                        />
                        <Label htmlFor="personnel-civil-servant">Devlet Memuru</Label>
                      </div>
                    )}
                  <div>
                    <Label htmlFor="personnel-phone">Telefon</Label>
                    {!canEditPhone && (
                      <p className="text-xs text-muted-foreground mb-1">
                        Yalnızca müdür ve süper yönetici düzenleyebilir (05XX XXX XX XX).
                      </p>
                    )}
                    <DebouncedInput
                      id="personnel-phone"
                      type="tel"
                      inputMode="numeric"
                      value={formatTrCepDisplay(formData.phone || '')}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, phone: normalizeTrCepToLocal11(value) }))
                      }
                      placeholder="0532 123 45 67"
                      disabled={isFormReadOnly || !canEditPhone}
                      className={isFormReadOnly || !canEditPhone ? "bg-gray-100" : ""}
                    />
                  </div>
                  <div>
                    <Label htmlFor="personnel-email">E-posta</Label>
                    <DebouncedInput
                      id="personnel-email"
                      type="email"
                      value={formData.email || ''}
                      onChange={(value) => setFormData(prev => ({ ...prev, email: value }))}
                      disabled={isFormReadOnly}
                      className={isFormReadOnly ? "bg-gray-100" : ""}
                    />
                  </div>
                  {(canSetPassword || canManageLoginBlock) && (
                    <div className="md:col-span-2 flex flex-wrap items-end gap-2">
                      {canSetPassword && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setSetPasswordOpen(true)}
                        >
                          <Lock className="h-4 w-4" /> Şifre ata / değiştir
                        </Button>
                      )}
                      {canIssueTemporaryLogin && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2 border-amber-300 text-amber-900"
                          onClick={handleIssueTemporaryLogin}
                          disabled={issueTempLoading || isFormReadOnly}
                        >
                          <KeyRound className="h-4 w-4" />
                          {issueTempLoading ? "Kod üretiliyor…" : "Tek kullanımlık giriş kodu"}
                        </Button>
                      )}
                      {canManageLoginBlock && (
                        <Button
                          type="button"
                          variant={formData.login_blocked ? "secondary" : "outline"}
                          size="sm"
                          className={`gap-2 ${formData.login_blocked ? "border-red-300 text-red-700" : "border-orange-300 text-orange-800"}`}
                          onClick={openLoginBlockConfirm}
                          disabled={loginBlockedLoading}
                        >
                          <Ban className="h-4 w-4" />
                          {formData.login_blocked ? "Giriş engelini kaldır" : "Girişi engelle"}
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* İzin Bilgileri */}
                <div className="border-t pt-4 mt-2">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-green-600" />
                    İzin Bilgileri
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <DatePicker
                        id="hire_date"
                        label="İşe Giriş Tarihi"
                        value={formData.hire_date || ''}
                        onChange={(value) => setFormData({...formData, hire_date: value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="annual_leave_days">Yıllık İzin Hakkı (Gün)</Label>
                      <Input id="annual_leave_days" type="number" min="0" max="60" value={formData.annual_leave_days ?? 20} onChange={(e) => setFormData({...formData, annual_leave_days: parseInt(e.target.value) || 0})} />
                    </div>
                    <div>
                      <Label htmlFor="used_leave_days" className="flex items-center gap-1">
                        Kullanılan İzin (Gün)
                        <span className="text-[10px] text-gray-400 font-normal">(otomatik)</span>
                      </Label>
                      <Input 
                        id="used_leave_days" 
                        type="number" 
                        min="0" 
                        value={formData.used_leave_days ?? 0} 
                        disabled
                        className="bg-gray-100 cursor-not-allowed"
                        title="Bu alan onaylanan izinlerden otomatik hesaplanır"
                      />
                    </div>
                  </div>
                  {(formData.annual_leave_days ?? 20) > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-800">Kalan Yıllık İzin:</span>
                        <Badge className={`text-sm ${((formData.annual_leave_days ?? 20) - (formData.used_leave_days || 0)) <= 5 ? 'bg-red-500' : 'bg-green-500'}`}>
                          {(formData.annual_leave_days ?? 20) - (formData.used_leave_days || 0)} gün
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>

                {/* Rol Seçimi */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-600" />
                    Rol ve Hiyerarşi
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Rol *</Label>
                      <SearchableSelect
                        value={formData.role_id || ''}
                        onChange={(roleIdValue) => {
                          const selected = getRoleById(roleIdValue);
                          const newSlug = selected?.slug || '';
                          setFormData((prev) => {
                            const roleSlugChanged = newSlug !== prev.role;
                            return {
                              ...prev,
                              role_id: roleIdValue,
                              role: newSlug,
                              // Rol değişince amir bağlantısı sıfırlanır (yeni seviye için yeniden seç)
                              parent_personnel_id: null,
                              // Yalnızca park görevlisi park atayabilir (kapsam dışı bölge mantığı korunur)
                              assigned_park_ids:
                                newSlug === 'park_gorevlisi' ? (prev.assigned_park_ids || []) : [],
                              // Ekipler role'e özgü (her ekip tek role bağlı); rol değişince
                              // temizle, aksi halde backend uyumsuz ekip için 400 döner.
                              ...(roleSlugChanged
                                ? { primary_team_id: '', secondary_team_id: '' }
                                : {}),
                            };
                          });
                        }}
                        showAllOption={false}
                        placeholder="Rol seçin"
                        searchPlaceholder="Rol ara..."
                        emptyText="Aktif rol yok"
                        options={hierarchyRoles.map((r) => ({ id: r.id, name: r.display_name || r.slug }))}
                      />
                    </div>

                    {/* Özel Rol Ata — yalnızca etiket; sistem rolleri etkilenmez */}
                    <div className="flex flex-col gap-2">
                      <Label className="opacity-0 hidden md:block">&nbsp;</Label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2 border-purple-300 text-purple-800 hover:bg-purple-50"
                          onClick={() => {
                            setCustomRoleDraft({
                              name: formData.custom_role_name || '',
                              description: formData.custom_role_description || '',
                            });
                            setCustomRoleDialogOpen(true);
                          }}
                          disabled={isFormReadOnly}
                        >
                          <Sparkles className="h-4 w-4" />
                          Özel Rol Ata
                        </Button>
                        {formData.custom_role_name && (
                          <Badge variant="secondary" className="gap-1 bg-purple-100 text-purple-900 border border-purple-200">
                            {formData.custom_role_name}
                            {!isFormReadOnly && (
                              <button
                                type="button"
                                onClick={() => setFormData((prev) => ({
                                  ...prev,
                                  custom_role_name: '',
                                  custom_role_description: '',
                                }))}
                                className="ml-1 opacity-60 hover:opacity-100"
                                aria-label="Özel rolü kaldır"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </Badge>
                        )}
                      </div>
                      {formData.custom_role_description && (
                        <p className="text-[11px] text-muted-foreground line-clamp-2">
                          {formData.custom_role_description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Bağlı Olduğu Amir + Atama Tipi — her zaman görünür, rol/kök duruma göre disabled */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>Bağlı Olduğu Amir</Label>
                      <SupervisorPicker
                        value={formData.parent_personnel_id || null}
                        onChange={(nextParent) => {
                          setFormData({
                            ...formData,
                            parent_personnel_id: nextParent,
                            // Amir temizlenirse Atama Tipi de NORMAL'a düşer
                            assignment_type: nextParent ? (formData.assignment_type || 'NORMAL') : 'NORMAL',
                          });
                        }}
                        eligibleSupervisors={eligibleSupervisors}
                        getRoleById={getRoleById}
                        getRoleBySlug={getRoleBySlug}
                        getRoleLabel={getRoleLabel}
                        disabled={isFormReadOnly || supervisorMode !== 'ready'}
                        placeholder={supervisorPlaceholder}
                      />
                      {supervisorHint && (
                        <p className={`mt-1 text-xs ${supervisorMode === 'no-role' ? 'text-amber-600' : 'text-slate-500'}`}>
                          {supervisorHint}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Atama Tipi</Label>
                      <SearchableSelect
                        value={formData.assignment_type || 'NORMAL'}
                        onChange={(value) =>
                          setFormData({ ...formData, assignment_type: value })
                        }
                        disabled={!formData.parent_personnel_id}
                        showAllOption={false}
                        placeholder="Atama tipi seçin"
                        searchPlaceholder="Atama tipi ara..."
                        options={[
                          { id: 'NORMAL', name: 'Normal' },
                          { id: 'GECICI', name: 'Geçici' },
                        ]}
                      />
                    </div>
                  </div>

                  {/* Ekip Atamaları — role bağlı (Migration 0066).
                      TeamPicker, o rol için ekip yoksa null döndürür. */}
                  {formData.role && (
                    <div className="mt-4 border-t pt-4">
                      <TeamPicker
                        roleSlug={formData.role}
                        primaryTeamId={formData.primary_team_id || null}
                        secondaryTeamId={formData.secondary_team_id || null}
                        onChange={({ primary, secondary }) =>
                          setFormData((prev) => ({
                            ...prev,
                            primary_team_id: primary,
                            secondary_team_id: secondary,
                          }))
                        }
                        disabled={isFormReadOnly}
                      />
                    </div>
                  )}
                </div>

                {/* Bölge/Park Atamaları */}
                {(formData.role === 'park_gorevlisi' || formData.role === 'formen' || formData.role === 'bolge_muhendisi' || formData.role === 'mudur_yardimcisi') && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      {(formData.role === "formen" || formData.role === "bolge_muhendisi" || formData.role === "mudur_yardimcisi")
                        ? "Bölge Atamaları"
                        : "Bölge ve Park Atamaları"}
                    </h4>
                    
                    {/* Bölge Seçimi */}
                    <div className="mb-4">
                      <Label className="flex items-center gap-2 mb-2">
                        <Home className="h-4 w-4" /> Sorumlu Bölgeler
                        <Button type="button" variant="ghost" size="sm" onClick={() => setIsQuickRegionDialogOpen(true)}>
                          <Plus className="h-3 w-3 mr-1" /> Yeni Bölge
                        </Button>
                      </Label>
                      <Input
                        type="text"
                        value={regionSearchQuery}
                        onChange={(e) => setRegionSearchQuery(e.target.value)}
                        placeholder="Bölge ara..."
                        className="mb-2"
                      />
                      <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-gray-50 max-h-32 overflow-y-auto">
                        {filteredRegions.length === 0 ? (
                          <p className="text-gray-500 text-sm">Aramaya uygun bölge bulunamadı</p>
                        ) : (
                          filteredRegions.map(region => (
                            <Badge
                              key={region.id}
                              variant={formData.region_ids?.includes(region.id) ? "default" : "outline"}
                              className="cursor-pointer hover:bg-blue-100"
                              onClick={() => handleRegionToggle(region.id)}
                            >
                              {region.name}
                              {formData.region_ids?.includes(region.id) && " ✓"}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Park seçimi sadece park görevlisi için */}
                    {formData.role === "park_gorevlisi" && (
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <TreePine className="h-4 w-4" /> Sorumlu Parklar
                          <Button type="button" variant="ghost" size="sm" onClick={() => setIsQuickParkDialogOpen(true)}>
                            <Plus className="h-3 w-3 mr-1" /> Yeni Park
                          </Button>
                        </Label>
                        <Input
                          type="text"
                          value={parkSearchQuery}
                          onChange={(e) => setParkSearchQuery(e.target.value)}
                          placeholder="Park ara..."
                          className="mb-2"
                        />
                        <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-gray-50 max-h-40 overflow-y-auto">
                          {sortedRegionScopedParks.length === 0 ? (
                            <p className="text-gray-500 text-sm">Önce bölge seçin veya park ekleyin</p>
                          ) : filteredParks.length === 0 ? (
                            <p className="text-gray-500 text-sm">Aramaya uygun park bulunamadı</p>
                          ) : (
                            filteredParks.map(park => (
                              <Badge
                                key={park.id}
                                variant={formData.assigned_park_ids?.includes(park.id) ? "default" : "outline"}
                                className="cursor-pointer hover:bg-green-100"
                                onClick={() => handleParkToggle(park.id)}
                              >
                                🌳 {park.name}
                                {formData.assigned_park_ids?.includes(park.id) && " ✓"}
                              </Badge>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Çalışma Saatleri */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-600" />
                    Çalışma Saatleri
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Sabah Başlangıç</Label>
                      <Input type="time" value={formData.work_schedule?.morning_start || '08:15'} onChange={(e) => setFormData({...formData, work_schedule: {...formData.work_schedule, morning_start: e.target.value}})} />
                    </div>
                    <div>
                      <Label>Sabah Bitiş</Label>
                      <Input type="time" value={formData.work_schedule?.morning_end || '11:45'} onChange={(e) => setFormData({...formData, work_schedule: {...formData.work_schedule, morning_end: e.target.value}})} />
                    </div>
                    <div>
                      <Label>Öğleden Sonra Başlangıç</Label>
                      <Input type="time" value={formData.work_schedule?.afternoon_start || '13:15'} onChange={(e) => setFormData({...formData, work_schedule: {...formData.work_schedule, afternoon_start: e.target.value}})} />
                    </div>
                    <div>
                      <Label>Öğleden Sonra Bitiş</Label>
                      <Input type="time" value={formData.work_schedule?.afternoon_end || '16:45'} onChange={(e) => setFormData({...formData, work_schedule: {...formData.work_schedule, afternoon_end: e.target.value}})} />
                    </div>
                  </div>
                </div>

                {/* Durum */}
                <div className="border-t pt-4">
                  <Label>Durum</Label>
                  <SearchableSelect
                    value={formData.status || 'active'}
                    onChange={(value) => setFormData({...formData, status: value})}
                    showAllOption={false}
                    placeholder="Seçin"
                    className="w-48"
                    searchPlaceholder="Durum ara..."
                    options={[{ id: 'active', name: 'Aktif' }, { id: 'passive', name: 'Pasif' }]}
                  />
                </div>

                {/* Kaydet Butonu */}
                <div className="flex justify-end gap-2 border-t pt-4 mt-4">
                  <Button type="button" variant="outline" onClick={handleClose}>İptal</Button>
                  {isFormReadOnly ? (
                    <div className="text-center text-amber-600 bg-amber-50 p-3 rounded-lg">
                      <span className="font-medium">Değişiklik yapma yetkiniz bulunmamaktadır</span>
                    </div>
                  ) : (
                    <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                      {loading ? 'Kaydediliyor...' : isEditing ? 'Güncelle' : 'Ekle'}
                    </Button>
                  )}
                </div>
              </form>
            </TabsContent>

            {/* ÖZET SEKMESİ */}
            {isEditing && (
              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* İşe Giriş Tarihi */}
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold text-blue-800">İşe Giriş Tarihi</h4>
                    </div>
                    {formData.hire_date ? (
                      <div>
                        <p className="text-2xl font-bold text-blue-900">
                          {new Date(formData.hire_date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                        <p className="text-sm text-blue-600 mt-1">
                          {Math.floor((new Date() - new Date(formData.hire_date)) / (1000 * 60 * 60 * 24 * 365))} yıldır çalışıyor
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-blue-600">Henüz girilmemiş</p>
                    )}
                  </div>

                  {/* Yıllık İzin Durumu */}
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-5 w-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">Yıllık İzin Durumu</h4>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-3xl font-bold ${((formData.annual_leave_days || 20) - (formData.used_leave_days || 0)) <= 5 ? 'text-red-600' : 'text-green-700'}`}>
                        {(formData.annual_leave_days || 20) - (formData.used_leave_days || 0)}
                      </span>
                      <span className="text-sm text-green-600">gün kalan</span>
                    </div>
                    <div className="mt-2 flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-full"></span>Toplam: {formData.annual_leave_days || 20} gün</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500 rounded-full"></span>Kullanılan: {formData.used_leave_days || 0} gün</span>
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${((formData.used_leave_days || 0) / (formData.annual_leave_days || 20)) > 0.8 ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(100, ((formData.used_leave_days || 0) / (formData.annual_leave_days || 20)) * 100)}%` }} />
                    </div>
                  </div>
                </div>

                {/* Bu Yıl İstatistikleri */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                    <p className="text-2xl font-bold text-red-600">{thisYearRaporDays}</p>
                    <p className="text-xs text-red-700">🏥 Bu Yıl Rapor Günü</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                    <p className="text-2xl font-bold text-purple-600">{thisYearMaterialCount}</p>
                    <p className="text-xs text-purple-700">📦 Bu Yıl Malzeme Talebi</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-center">
                    <p className="text-2xl font-bold text-orange-600">{totalMaterialsReceived}</p>
                    <p className="text-xs text-orange-700">✅ Toplam Alınan Malzeme</p>
                  </div>
                  <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 text-center">
                    <p className="text-2xl font-bold text-cyan-600">{assignedParks.length}</p>
                    <p className="text-xs text-cyan-700">🌳 Sorumlu Park</p>
                  </div>
                </div>

                {/* Amir Zinciri (dinamik — parent_personnel_id'den yukarı) */}
                <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-5 w-5 text-indigo-600" />
                    <h4 className="font-semibold text-indigo-800">Amir Zinciri</h4>
                  </div>
                  {parentChain.length === 0 ? (
                    <div className="text-sm text-indigo-600 bg-white px-3 py-2 rounded-lg border">
                      Üst amir bulunmuyor — hiyerarşi kökü
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      {parentChain.map((node, idx) => (
                        <React.Fragment key={node.id}>
                          {idx > 0 && (
                            <span className="text-indigo-400 text-lg">→</span>
                          )}
                          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border shadow-sm">
                            <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-sm font-bold">
                              {node.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">
                                {node.role_display_name || node.role_slug}
                              </p>
                              <p className="text-sm font-medium">{node.name}</p>
                            </div>
                            {node.assignment_type === 'GECICI' && (
                              <Badge className="bg-yellow-500 text-white text-[10px]">Geçici</Badge>
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            )}

            {/* İZİNLER SEKMESİ */}
            {isEditing && (
              <TabsContent value="leaves" className="space-y-4">
                {/* Kalan İzin Günleri Kartları - Devir dahil */}
                {(() => {
                  const carryoverDays = formData.carryover_days || 0;
                  const usedCarryover = formData.used_carryover_days || 0;
                  const remainingCarryover = carryoverDays - usedCarryover;
                  const annualDays = formData.annual_leave_days || 20;
                  const usedAnnual = formData.used_leave_days || 0;
                  const remainingAnnual = annualDays - usedAnnual;
                  const totalAvailable = remainingCarryover + remainingAnnual;
                  
                  return (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                      {/* Toplam Kullanılabilir */}
                      <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200 text-center">
                        <p className="text-[10px] text-green-600 mb-1">✅ Toplam Kullanılabilir</p>
                        <p className="text-xl font-bold text-green-700">{totalAvailable}</p>
                        <p className="text-[10px] text-gray-500">gün</p>
                      </div>
                      
                      {/* Devir (varsa) */}
                      {carryoverDays > 0 && (
                        <div className="p-3 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-200 text-center">
                          <p className="text-[10px] text-amber-600 mb-1">📦 Devir ({formData.carryover_from_year}&apos;den)</p>
                          <p className="text-xl font-bold text-amber-700">{remainingCarryover}/{carryoverDays}</p>
                          <p className="text-[10px] text-gray-500">gün kalan</p>
                        </div>
                      )}
                      
                      {/* Bu Yıl Yıllık İzin */}
                      <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 text-center">
                        <p className="text-[10px] text-blue-600 mb-1">🏖️ Bu Yıl Yıllık</p>
                        <p className="text-xl font-bold text-blue-700">{remainingAnnual}/{annualDays}</p>
                        <p className="text-[10px] text-gray-500">gün kalan</p>
                      </div>
                      
                      {/* Sosyal İzin */}
                      <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 text-center">
                        <p className="text-[10px] text-purple-600 mb-1">👨‍👩‍👧‍👦 Sosyal İzin</p>
                        <p className="text-xl font-bold text-purple-700">{12 - socialUsedDays}/12</p>
                        <p className="text-[10px] text-gray-500">gün kalan</p>
                      </div>
                      
                      {/* Rapor */}
                      <div className="p-3 bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg border border-pink-200 text-center">
                        <p className="text-[10px] text-pink-600 mb-1">🏥 Rapor</p>
                        <p className="text-xl font-bold text-pink-700">{40 - (formData.used_report_days || 0)}/40</p>
                        <p className="text-[10px] text-gray-500">gün kalan</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Yıllık İzin Özeti - Şu anki yıl + Geçmiş yıl */}
                {(() => {
                  const currentYear = new Date().getFullYear().toString();
                  const previousYear = (new Date().getFullYear() - 1).toString();
                  
                  // Şu anki yılın verileri (canlı)
                  const currentYearData = {
                    annual_leave_days: formData.annual_leave_days || 20,
                    used_leave_days: formData.used_leave_days || 0,
                    carryover_days: formData.carryover_days || 0,
                    used_carryover_days: formData.used_carryover_days || 0,
                    carryover_from_year: formData.carryover_from_year,
                    used_social_leave_days: socialUsedDays,
                    used_report_days: formData.used_report_days || 0
                  };
                  
                  // Geçmiş yılın verileri (leave_history'den)
                  const previousYearData = formData.leave_history?.[previousYear] || null;
                  
                  return (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm font-semibold text-amber-800 mb-2">📜 Yıllık İzin Özeti ({currentYear} - {previousYear})</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {/* Şu anki yıl */}
                        <div className="bg-white p-2 rounded border border-blue-200">
                          <p className="text-xs font-bold text-blue-700">{currentYear} Yılı (Güncel)</p>
                          <div className="text-[10px] text-gray-600 mt-1 space-y-0.5">
                            <p>🏖️ Yıllık: {currentYearData.used_leave_days}/{currentYearData.annual_leave_days} kullanıldı</p>
                            {currentYearData.carryover_days > 0 && (
                              <p>📦 {currentYearData.carryover_from_year}&apos;den devir: {currentYearData.used_carryover_days}/{currentYearData.carryover_days} kullanıldı</p>
                            )}
                            <p>👨‍👩‍👧‍👦 Sosyal: {currentYearData.used_social_leave_days}/12 kullanıldı</p>
                            <p>🏥 Rapor: {currentYearData.used_report_days}/40 kullanıldı</p>
                          </div>
                        </div>
                        
                        {/* Geçmiş yıl */}
                        {previousYearData ? (
                          <div className="bg-white p-2 rounded border border-amber-100">
                            <p className="text-xs font-bold text-amber-700">{previousYear} Yılı</p>
                            <div className="text-[10px] text-gray-600 mt-1 space-y-0.5">
                              <p>🏖️ Yıllık: {previousYearData.used_leave_days || 0}/{previousYearData.annual_leave_days || 20} kullanıldı</p>
                              {(previousYearData.carryover_days > 0) && (
                                <p>📦 O yıla devir: {previousYearData.used_carryover_days || 0}/{previousYearData.carryover_days} kullanıldı</p>
                              )}
                              {(previousYearData.carryover_to_next_year > 0) && (
                                <p className="text-green-600">➡️ {currentYear}&apos;e devir: {previousYearData.carryover_to_next_year} gün</p>
                              )}
                              <p>👨‍👩‍👧‍👦 Sosyal: {previousYearData.used_social_leave_days || 0}/12 kullanıldı</p>
                              <p>🏥 Rapor: {previousYearData.used_report_days || 0}/40 kullanıldı</p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white p-2 rounded border border-gray-100">
                            <p className="text-xs font-bold text-gray-500">{previousYear} Yılı</p>
                            <p className="text-[10px] text-gray-400 mt-1">Veri bulunamadı</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {personnelLeaves.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Henüz izin talebi bulunmuyor</p>
                  </div>
                ) : (
                  <>
                    {/* İzin Detayları Başlık */}
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-700">📋 İzin Talep Detayları ({personnelLeaves.length} talep)</p>
                    </div>
                    
                    <Accordion type="multiple" className="space-y-2">
                    {leaveTypes.map(leaveType => {
                      const leaves = groupedLeaves[leaveType];
                      const approved = leaves.filter(l => l.status === 'approved').length;
                      const pending = leaves.filter(l => l.status === 'in_progress' || l.status === 'pending').length;
                      const rejected = leaves.filter(l => l.status === 'rejected').length;
                      const style = getLeaveTypeStyle(leaveType);
                      return (
                        <AccordionItem key={leaveType} value={leaveType} className={`border rounded-lg overflow-hidden ${style.border}`}>
                          <AccordionTrigger className={`px-4 py-3 hover:bg-gray-50 bg-gradient-to-r ${style.color}`}>
                            <div className="flex items-center justify-between w-full pr-4">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{style.emoji}</span>
                                <div className="text-left"><h4 className="font-semibold">{leaveType}</h4><p className="text-xs text-gray-600">{leaves.length} talep</p></div>
                              </div>
                              <div className="flex items-center gap-2">
                                {approved > 0 && <Badge variant="outline" className="bg-green-50 border-green-300 text-green-700 text-xs">✓ {approved}</Badge>}
                                {pending > 0 && <Badge variant="outline" className="bg-yellow-50 border-yellow-300 text-yellow-700 text-xs">⏳ {pending}</Badge>}
                                {rejected > 0 && <Badge variant="outline" className="bg-red-50 border-red-300 text-red-700 text-xs">✕ {rejected}</Badge>}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-3 mt-2">
                              {leaves.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((leave) => {
                                // Hafta içi/sonu hesaplama
                                const startDate = new Date(leave.start_date);
                                const endDate = new Date(leave.end_date);
                                let weekdays = 0;
                                let weekends = 0;
                                let current = new Date(startDate);
                                while (current <= endDate) {
                                  const dayOfWeek = current.getDay();
                                  if (dayOfWeek === 0 || dayOfWeek === 6) {
                                    weekends++;
                                  } else {
                                    weekdays++;
                                  }
                                  current.setDate(current.getDate() + 1);
                                }

                                const toValidNumber = (value) => {
                                  const n = Number(value);
                                  return Number.isFinite(n) ? n : null;
                                };
                                const toPositiveNumber = (value) => {
                                  const n = Number(value);
                                  return Number.isFinite(n) && n > 0 ? n : null;
                                };
                                const plannedAnnualDays = toPositiveNumber(leave?.annual_days_planned) ?? toPositiveNumber(leave?.workdays_used) ?? weekdays;
                                const earlyReturnDate = leave?.actual_return_date || leave?.return_request?.actual_return_date;
                                const returnRequestStatus = String(leave?.return_request?.status || '').trim().toLowerCase();
                                const hasApprovedEarlyReturn =
                                  leaveType === 'Yıllık İzin' &&
                                  leave.status === 'approved' &&
                                  Boolean(earlyReturnDate) &&
                                  returnRequestStatus === 'approved';
                                const effectiveAnnualDays = hasApprovedEarlyReturn
                                  ? (toPositiveNumber(leave?.leave_days_used_actual) ?? plannedAnnualDays)
                                  : plannedAnnualDays;
                                const holidayWeekdayCount = Math.max(0, weekdays - plannedAnnualDays);
                                const isEarlyReturnAdjusted =
                                  hasApprovedEarlyReturn &&
                                  effectiveAnnualDays < plannedAnnualDays;
                                
                                return (
                                  <div key={leave.id} className={`p-3 rounded-lg border-l-4 ${leave.status === 'approved' ? 'bg-green-50 border-green-500' : leave.status === 'rejected' ? 'bg-red-50 border-red-500' : 'bg-yellow-50 border-yellow-500'}`}>
                                    {/* Üst satır - Durum ve Tarih */}
                                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <Badge className={leave.status === 'approved' ? 'bg-green-600' : leave.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'}>
                                          {leave.status === 'approved' ? '✓ Onaylandı' : leave.status === 'rejected' ? '✕ Reddedildi' : '⏳ Bekliyor'}
                                        </Badge>
                                        <span className="text-sm font-medium text-gray-700">
                                          {new Date(leave.start_date).toLocaleDateString('tr-TR')} - {new Date(leave.end_date).toLocaleDateString('tr-TR')}
                                        </span>
                                      </div>
                                      <span className="text-sm font-bold text-gray-800 bg-gray-200 px-2 py-1 rounded">
                                        {leave.total_days || leave.workdays_used || '-'} gün
                                      </span>
                                    </div>
                                    
                                    {/* Detay satırı - Hafta içi/sonu ve düşülen izin */}
                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                        📅 {weekdays} hafta içi
                                      </span>
                                      {weekends > 0 && (
                                        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                          🗓️ {weekends} hafta sonu
                                        </span>
                                      )}
                                      {leave.status === 'approved' && leaveType === 'Yıllık İzin' && (
                                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                          ✅ Yıllık izinden {effectiveAnnualDays} gün düşüldü{holidayWeekdayCount > 0 ? ` • ${holidayWeekdayCount} gün bayram tatili` : ''}
                                        </span>
                                      )}
                                      {leave.status === 'approved' && leaveType === 'Sosyal İzin' && (
                                        <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                          ✅ Sosyal izinden {leave.total_days || weekdays} gün düşüldü
                                        </span>
                                      )}
                                      {leave.status === 'approved' && leaveType === 'Rapor' && (
                                        <span className="bg-pink-100 text-pink-700 px-2 py-0.5 rounded">
                                          ✅ Rapor günlerinden {leave.total_days} gün düşüldü
                                        </span>
                                      )}

                                      {/* Rapor fotoğrafı — rozet satırında KÜÇÜK belge simgesi.
                                          Önce tam genişlikte önizleme vardı; listede gereksiz yer
                                          kaplıyordu. Artık diğer rozetlerle aynı satırda duruyor,
                                          tıklanınca uygulama içi görüntüleyicide büyür.
                                          Koşul TÜR değil DOSYA VARLIĞI: rapor başka bir türe
                                          iliştirilmişse de erişilebilir kalır. */}
                                      {leave.report_file && (
                                        <button
                                          type="button"
                                          onClick={() => setReportViewerLeaveId(leave.id)}
                                          title={leave.report_filename ? `Raporu büyüt — ${leave.report_filename}` : 'Raporu büyüt'}
                                          className="inline-flex items-center gap-1 bg-white text-pink-800 border border-pink-300 pl-0.5 pr-2 py-0.5 rounded cursor-zoom-in hover:bg-pink-50 hover:border-pink-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500"
                                        >
                                          <img
                                            src={leave.report_file}
                                            alt=""
                                            aria-hidden="true"
                                            className="h-6 w-[18px] object-cover rounded-[2px] border border-pink-200 bg-white"
                                          />
                                          <span className="font-medium">Rapor</span>
                                          <span aria-hidden="true">🔍</span>
                                        </button>
                                      )}
                                    </div>

                                    {isEarlyReturnAdjusted && (
                                      <div className="mt-2 text-[11px] leading-relaxed bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-1">
                                        {`${startDate.toLocaleDateString('tr-TR').replaceAll('.', ',')}-${endDate.toLocaleDateString('tr-TR').replaceAll('.', ',')} tarihleri arası ${plannedAnnualDays} gün yıllık${holidayWeekdayCount > 0 ? ` ${holidayWeekdayCount} gün bayram tatili` : ''} olan izin talebiniz ${(earlyReturnDate ? new Date(earlyReturnDate).toLocaleDateString('tr-TR').replaceAll('.', ',') : '-') } tarihinde erken işbaşı yaptığınız için ${effectiveAnnualDays} gün olarak yıllık izin talebinden düşülmüştür.`}
                                      </div>
                                    )}
                                    
                                    {/* Açıklama (varsa) */}
                                    {leave.reason && (
                                      <p className="text-xs text-gray-500 mt-2 italic">📝 {leave.reason}</p>
                                    )}

                                    {leave.report_file && (
                                      <ImageLightbox
                                        open={reportViewerLeaveId === leave.id}
                                        onClose={() => setReportViewerLeaveId(null)}
                                        src={leave.report_file}
                                        alt={`Rapor — ${formData.name || ''}`.trim()}
                                        filename={leave.report_filename}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                  </>
                )}
              </TabsContent>
            )}

            {/* MALZEMELER SEKMESİ */}
            {isEditing && (
              <TabsContent value="materials" className="space-y-4">
                {personnelMaterials.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                    <Package className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg">Henüz malzeme talebi bulunmuyor</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                        <p className="text-xl font-bold text-purple-600">{personnelMaterials.length}</p><p className="text-xs text-purple-700">Toplam Talep</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                        <p className="text-xl font-bold text-green-600">{personnelMaterials.filter(m => m.status === 'delivered').length}</p><p className="text-xs text-green-700">Teslim Edilen</p>
                      </div>
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
                        <p className="text-xl font-bold text-yellow-600">{personnelMaterials.filter(m => m.status !== 'delivered').length}</p><p className="text-xs text-yellow-700">Bekleyen</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                        <p className="text-xl font-bold text-blue-600">{totalMaterialsReceived}</p><p className="text-xs text-blue-700">Toplam Adet</p>
                      </div>
                    </div>
                    <Accordion type="multiple" className="space-y-2">
                      {materialTypes.map(materialType => {
                        const materials = groupedMaterials[materialType];
                        const delivered = materials.filter(m => m.status === 'delivered').length;
                        const totalQty = materials.reduce((sum, m) => sum + m.quantity, 0);
                        return (
                          <AccordionItem key={materialType} value={materialType} className="border rounded-lg overflow-hidden border-purple-300">
                            <AccordionTrigger className="px-4 py-3 hover:bg-gray-50 bg-gradient-to-r from-purple-50 to-purple-100">
                              <div className="flex items-center justify-between w-full pr-4">
                                <div className="flex items-center gap-3">
                                  <span className="text-2xl">{getMaterialIcon(materialType)}</span>
                                  <div className="text-left"><h4 className="font-semibold">{materialType}</h4><p className="text-xs text-gray-600">{totalQty} adet</p></div>
                                </div>
                                {delivered > 0 && <Badge variant="outline" className="bg-green-50 border-green-300 text-green-700 text-xs">✓ {delivered} Teslim</Badge>}
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="space-y-3 mt-2">
                                {materials.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((mat) => (
                                  <div key={mat.id} className={`p-3 rounded-lg border-l-4 ${mat.status === 'delivered' ? 'bg-green-50 border-green-500' : mat.status === 'rejected' ? 'bg-red-50 border-red-500' : 'bg-yellow-50 border-yellow-500'}`}>
                                    {/* Üst satır - Durum ve Miktar */}
                                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                                      <div className="flex items-center gap-2">
                                        <Badge className={mat.status === 'delivered' ? 'bg-green-600' : mat.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'}>
                                          {mat.status === 'delivered' ? '✓ Teslim Edildi' : mat.status === 'rejected' ? '✕ Reddedildi' : '⏳ Bekliyor'}
                                        </Badge>
                                        <span className="text-sm font-bold bg-white px-2 py-0.5 rounded border">{mat.quantity} Adet</span>
                                      </div>
                                      <span className="text-xs text-gray-500">
                                        📅 Talep: {new Date(mat.created_at || mat.requested_date).toLocaleDateString('tr-TR')}
                                      </span>
                                    </div>
                                    
                                    {/* Detay satırı */}
                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                      {mat.status === 'delivered' && (
                                        <>
                                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                            ✅ Teslim: {mat.delivered_date ? new Date(mat.delivered_date).toLocaleDateString('tr-TR') : '-'}
                                          </span>
                                          {mat.delivered_by_name && (
                                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                              👤 Teslim Eden: {mat.delivered_by_name}
                                            </span>
                                          )}
                                        </>
                                      )}
                                      {mat.status === 'rejected' && (
                                        <>
                                          <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                            ❌ Red: {mat.rejected_date ? new Date(mat.rejected_date).toLocaleDateString('tr-TR') : '-'}
                                          </span>
                                          {mat.rejection_reason && (
                                            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                              📝 Sebep: {mat.rejection_reason}
                                            </span>
                                          )}
                                        </>
                                      )}
                                      {mat.formen_name && mat.status !== 'delivered' && (
                                        <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                          👷 Formen: {mat.formen_name}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Açıklama (varsa) */}
                                    {mat.description && (
                                      <p className="text-xs text-gray-500 mt-2 italic">📝 {mat.description}</p>
                                    )}
                                    {mat.delivery_note && (
                                      <p className="text-xs text-green-600 mt-1 italic">💬 Teslim Notu: {mat.delivery_note}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </>
                )}
              </TabsContent>
            )}

            {/* GÖREVLER SEKMESİ - Görev Geçmişi */}
            {isEditing && (
              <TabsContent value="tasks" className="space-y-4">
                {/* Filtre Alanı */}
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex-1 min-w-[260px]">
                        <Label className="text-xs text-blue-700 mb-1 block">📅 Tarih Aralığı</Label>
                        <DateRangePicker
                          startDate={taskFilterStartDate}
                          endDate={taskFilterEndDate}
                          onChange={({ start, end }) => {
                            setTaskFilterStartDate(start);
                            setTaskFilterEndDate(end);
                          }}
                          placeholder="Tarih aralığı seç"
                          highlightHolidays
                        />
                      </div>
                      <div className="min-w-[180px]">
                        <Label className="text-xs text-blue-700 mb-1 block">🏞️ Park</Label>
                        <SearchableSelect
                          value={taskFilterParkId}
                          onChange={setTaskFilterParkId}
                          allLabel="Tüm Parklar"
                          allValue="all"
                          className="h-9 text-sm"
                          searchPlaceholder="Park ara..."
                          options={assignedParks.map(park => ({ id: park.id, name: park.name }))}
                        />
                      </div>
                      <Button 
                        onClick={() => {
                          // Uygulanan filtreleri güncelle
                          setAppliedStartDate(taskFilterStartDate);
                          setAppliedEndDate(taskFilterEndDate);
                          setAppliedParkId(taskFilterParkId);
                          // Veriyi çek
                          fetchTaskHistory(formData.id, taskFilterStartDate, taskFilterEndDate);
                        }}
                        className="h-9 bg-blue-600 hover:bg-blue-700"
                      >
                        🔍 Ara
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {taskHistoryLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                    <p className="text-gray-500">Görev geçmişi yükleniyor...</p>
                  </div>
                ) : !taskHistory ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                    <ClipboardList className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg">Görev geçmişi bulunamadı</p>
                    <Button variant="outline" className="mt-4" onClick={() => fetchTaskHistory(formData.id, appliedStartDate, appliedEndDate)}>
                      Yenile
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Özet İstatistikler - Uygulanan filtrelere göre */}
                    {(() => {
                      const normalizeDate = (val) => (typeof val === 'string' ? val.slice(0, 10) : '');
                      const normalizeText = (val) =>
                        String(val || '')
                          .toLocaleLowerCase('tr-TR')
                          .replace(/\s+/g, ' ')
                          .trim();
                      const getDelegationInfo = (date, park) => {
                        if (!date || !park) return null;
                        const dateKey = normalizeDate(date);
                        const parkId = park?.id || park?.park_id;
                        const parkNameNorm = normalizeText(park?.name || park?.park_name);
                        const leave = personnelLeaves.find((lr) => {
                          if (!lr || (lr.status || '') !== 'approved') return false;
                          const start = normalizeDate(lr.start_date);
                          const end = normalizeDate(lr.end_date || lr.return_date);
                          if (!start || !end || !(start <= dateKey && dateKey <= end)) return false;
                          const subs = Array.isArray(lr.park_substitutes) ? lr.park_substitutes : [];
                          return subs.some((ps) => {
                            const psParkId = ps?.park_id || ps?.id;
                            const psParkNameNorm = normalizeText(ps?.park_name || ps?.name);
                            return (parkId && psParkId && String(parkId) === String(psParkId)) ||
                              (parkNameNorm && psParkNameNorm && parkNameNorm === psParkNameNorm);
                          });
                        });
                        if (!leave) return null;
                        const subs = Array.isArray(leave.park_substitutes) ? leave.park_substitutes : [];
                        const matched = subs.find((ps) => {
                          const psParkId = ps?.park_id || ps?.id;
                          const psParkNameNorm = normalizeText(ps?.park_name || ps?.name);
                          return (parkId && psParkId && String(parkId) === String(psParkId)) ||
                            (parkNameNorm && psParkNameNorm && parkNameNorm === psParkNameNorm);
                        });
                        const delegateName =
                          matched?.substitute_name ||
                          matched?.substitute_personnel_name ||
                          leave?.substitute_personnel_name ||
                          'Vekil';
                        return {
                          delegate_name: delegateName,
                          reason_type: leave.reason_type
                        };
                      };

                      // Filtrelenmiş daily_stats - uygulanan filtreleri kullan
                      const filteredStats = (taskHistory.daily_stats || []).filter(day => {
                        const dateInRange = day.date >= appliedStartDate && day.date <= appliedEndDate;
                        const parkMatch = appliedParkId === 'all' || 
                          assignedParks.find(p => p.id === appliedParkId)?.name === day.park_name;
                        return dateInRange && parkMatch;
                      });
                      
                      // Tarih aralığındaki tüm günleri oluştur
                      const getAllDatesInRange = (start, end) => {
                        const dates = [];
                        const startDate = new Date(start);
                        const endDate = new Date(end);
                        const current = new Date(startDate);
                        
                        while (current <= endDate) {
                          dates.push(current.toISOString().split('T')[0]);
                          current.setDate(current.getDate() + 1);
                        }
                        return dates;
                      };
                      
                      const allDatesInRange = getAllDatesInRange(appliedStartDate, appliedEndDate);
                      
                      // Tarihlere göre grupla (backend'den gelen veriler)
                      const groupedByDate = filteredStats.reduce((acc, day) => {
                        if (!acc[day.date]) {
                          acc[day.date] = [];
                        }
                        acc[day.date].push(day);
                        return acc;
                      }, {});
                      
                      // Tüm tarihleri grupla (görev yapılmamış günler dahil)
                      allDatesInRange.forEach(date => {
                        if (!groupedByDate[date]) {
                          groupedByDate[date] = []; // Boş array - görev yapılmamış gün
                        }
                      });
                      
                      const sortedDates = Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a)); // En yeni önce
                      
                      // Özet hesapla - tüm günler ve tüm parklar için
                      const filteredAssignedParksForSummary = appliedParkId === 'all' 
                        ? assignedParks 
                        : assignedParks.filter(p => p.id === appliedParkId);
                      
                      const totalTasksPerParkPerDay = 6; // Varsayılan görev sayısı
                      const totalPossibleTasks = allDatesInRange.length * filteredAssignedParksForSummary.length * totalTasksPerParkPerDay;
                      const completedTasks = filteredStats.reduce((sum, d) => sum + (d.completed || 0), 0);
                      const lateTasks = filteredStats.reduce((sum, d) => sum + (d.late || 0), 0);
                      
                      const filteredSummary = {
                        total_tasks: totalPossibleTasks,
                        completed_tasks: completedTasks,
                        late_tasks: lateTasks,
                      };
                      filteredSummary.completion_rate = filteredSummary.total_tasks > 0 
                        ? Math.round((filteredSummary.completed_tasks / filteredSummary.total_tasks) * 100) 
                        : 0;

                      return (
                        <>
                          {/* Özet Kartları */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                              <CardContent className="p-4 text-center">
                                <p className="text-sm text-blue-600">Toplam Görev</p>
                                <p className="text-2xl font-bold text-blue-800">{filteredSummary.total_tasks}</p>
                              </CardContent>
                            </Card>
                            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                              <CardContent className="p-4 text-center">
                                <p className="text-sm text-green-600">Tamamlanan</p>
                                <p className="text-2xl font-bold text-green-800">{filteredSummary.completed_tasks}</p>
                              </CardContent>
                            </Card>
                            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                              <CardContent className="p-4 text-center">
                                <p className="text-sm text-red-600">Geciken</p>
                                <p className="text-2xl font-bold text-red-800">{filteredSummary.late_tasks}</p>
                              </CardContent>
                            </Card>
                            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                              <CardContent className="p-4 text-center">
                                <p className="text-sm text-purple-600">Tamamlanma</p>
                                <p className="text-2xl font-bold text-purple-800">%{filteredSummary.completion_rate}</p>
                              </CardContent>
                            </Card>
                          </div>

                          {/* Performans Çubuğu */}
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm flex items-center gap-2">
                                <TrendingUp className="h-4 w-4" />
                                Performans Özeti ({appliedStartDate} - {appliedEndDate})
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-3">
                                <div>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span>Tamamlanma Oranı</span>
                                    <span className="font-medium">%{filteredSummary.completion_rate}</span>
                                  </div>
                                  <Progress value={filteredSummary.completion_rate} className="h-2" />
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Günlük Detaylar - Akordeon Yapısı */}
                          {sortedDates.length > 0 && (
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                  <Calendar className="h-4 w-4" />
                                  Günlük Detaylar ({sortedDates.length} gün)
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <Accordion 
                                  type="multiple" 
                                  value={expandedDates}
                                  onValueChange={setExpandedDates}
                                  className="space-y-2"
                                >
                                  {sortedDates.map((date) => {
                                    const dayParks = groupedByDate[date];
                                    
                                    // Personele bağlı tüm parkları dahil et - uygulanan filtre
                                    const filteredAssignedParks = appliedParkId === 'all' 
                                      ? assignedParks 
                                      : assignedParks.filter(p => p.id === appliedParkId);
                                    
                                    // Her bağlı park için veri oluştur (görev yapılmamış parklar dahil)
                                    const allParksForDay = filteredAssignedParks.map(park => {
                                      const existingData = dayParks.find(dp => dp.park_name === park.name || dp.park_id === park.id);
                                      const delegationInfo = getDelegationInfo(date, park);
                                      if (existingData) {
                                        return {
                                          ...existingData,
                                          park_id: existingData.park_id || park.id,
                                          delegation_info: delegationInfo
                                        };
                                      }
                                      // Görev yapılmamış park - varsayılan görevlerle göster
                                      const defaultTasks = [
                                        { task_name: 'Sabah Mıntıkası', completed: false },
                                        { task_name: 'Öğle Mıntıkası', completed: false },
                                        { task_name: 'Akşam Mıntıkası', completed: false },
                                        { task_name: 'Sert Zemin Temizliği', completed: false },
                                        { task_name: 'Yaprak/Çöp Temizliği', completed: false },
                                        { task_name: 'Sulama', completed: false }
                                      ];
                                      return {
                                        date: date,
                                        park_id: park.id,
                                        park_name: park.name,
                                        total: 6,
                                        completed: 0,
                                        late: 0,
                                        completion_rate: 0,
                                        no_task_done: true, // Hiç görev yapılmamış işareti
                                        default_tasks: defaultTasks,
                                        delegation_info: delegationInfo
                                      };
                                    });
                                    
                                    const dayTotal = allParksForDay.reduce((sum, d) => sum + (d.total || 0), 0);
                                    const dayCompleted = allParksForDay.reduce((sum, d) => sum + (d.completed || 0), 0);
                                    const dayRate = dayTotal > 0 ? Math.round((dayCompleted / dayTotal) * 100) : 0;
                                    
                                    return (
                                      <AccordionItem key={date} value={date} className="border rounded-lg">
                                        <AccordionTrigger className="px-4 py-3 hover:bg-gray-50">
                                          <div className="flex items-center justify-between w-full pr-4">
                                            <div className="flex items-center gap-3">
                                              <span className="text-lg">📅</span>
                                              <span className="font-medium">{date}</span>
                                              <Badge variant="outline" className="text-xs">
                                                {allParksForDay.length} park
                                              </Badge>
                                            </div>
                                            <div className="flex items-center gap-3">
                                              <span className="text-sm">
                                                <span className="text-green-600 font-medium">{dayCompleted}</span>
                                                <span className="text-gray-400">/{dayTotal}</span>
                                              </span>
                                              <Badge className={`text-xs ${dayRate >= 80 ? 'bg-green-100 text-green-700' : dayRate >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                                %{dayRate}
                                              </Badge>
                                            </div>
                                          </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-4 pb-4">
                                          <div className="space-y-3">
                                            {allParksForDay.map((parkDay, idx) => {
                                              const dayCompletion = taskHistory.task_completions?.find(tc => 
                                                tc.date === parkDay.date && (tc.park_name === parkDay.park_name || tc.park_id === parkDay.park_id)
                                              );
                                              const isSelected = selectedDayDetail?.date === parkDay.date && 
                                                selectedDayDetail?.park_name === parkDay.park_name;
                                              
                                              // Görev listesi: varsa gerçek, yoksa varsayılan
                                              const taskList = dayCompletion?.tasks || parkDay.default_tasks || [];
                                              
                                              return (
                                                <div key={idx} className={`border rounded-lg overflow-hidden ${parkDay.no_task_done ? 'border-red-300 bg-red-50/50' : ''}`}>
                                                  <div 
                                                    className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                                                      parkDay.no_task_done ? 'bg-red-50 hover:bg-red-100' :
                                                      isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                                                    }`}
                                                    onClick={() => setSelectedDayDetail(isSelected ? null : { ...parkDay, tasks: taskList })}
                                                  >
                                                    <div className="flex items-center gap-2">
                                                      <span className="text-lg">{parkDay.no_task_done ? '⚠️' : '🏞️'}</span>
                                                      <div className="flex flex-col gap-1">
                                                        <span className={`font-medium text-sm ${parkDay.no_task_done ? 'text-red-700' : ''}`}>
                                                          {parkDay.park_name}
                                                        </span>
                                                        {parkDay?.delegation_info?.delegate_name && (
                                                          <span className="text-[11px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 inline-flex w-fit">
                                                            Vekalet: {parkDay.delegation_info.delegate_name}
                                                            {parkDay?.delegation_info?.reason_type ? ` (${parkDay.delegation_info.reason_type})` : ''}
                                                          </span>
                                                        )}
                                                      </div>
                                                      {parkDay.no_task_done && (
                                                        <Badge className="bg-red-500 text-white text-xs">Görev Yapılmadı</Badge>
                                                      )}
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                      <span className="text-sm">
                                                        <span className={parkDay.completed > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                                          {parkDay.completed}
                                                        </span>
                                                        <span className="text-gray-400">/{parkDay.total}</span>
                                                      </span>
                                                      {parkDay.late > 0 && (
                                                        <Badge className="bg-red-100 text-red-700 text-xs">{parkDay.late} geç</Badge>
                                                      )}
                                                      <Badge variant="outline" className={`text-xs ${
                                                        parkDay.completion_rate >= 80 ? 'bg-green-50 text-green-700' : 
                                                        parkDay.completion_rate >= 50 ? 'bg-yellow-50 text-yellow-700' : 
                                                        'bg-red-50 text-red-700'
                                                      }`}>
                                                        %{parkDay.completion_rate}
                                                      </Badge>
                                                      <span className="text-gray-400 text-xs">{isSelected ? '▲' : '▼'}</span>
                                                    </div>
                                                  </div>
                                                  
                                                  {/* Görev Detayları */}
                                                  {isSelected && taskList.length > 0 && (
                                                    <div className="p-3 bg-gray-50 border-t space-y-2">
                                                      <p className="text-xs text-gray-500 font-medium mb-2">
                                                        📋 Görev Listesi ({parkDay.completed}/{parkDay.total}):
                                                      </p>
                                                      {taskList.map((task, taskIdx) => (
                                                        <div 
                                                          key={taskIdx} 
                                                          className={`flex items-center justify-between p-2 rounded-lg ${
                                                            task.completed 
                                                              ? 'bg-green-50 border border-green-200' 
                                                              : 'bg-red-50 border border-red-200'
                                                          }`}
                                                        >
                                                          <div className="flex items-center gap-2">
                                                            {task.completed ? (
                                                              <CheckCircle className="h-4 w-4 text-green-600" />
                                                            ) : (
                                                              <XCircle className="h-4 w-4 text-red-600" />
                                                            )}
                                                            <span className={`text-sm ${task.completed ? 'text-green-700' : 'text-red-700 font-medium'}`}>
                                                              {task.task_name}
                                                            </span>
                                                          </div>
                                                          {task.completed && task.completed_at && (
                                                            <span className="text-xs text-gray-500">
                                                              ⏰ {new Date(task.completed_at).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}
                                                            </span>
                                                          )}
                                                          {!task.completed && (
                                                            <span className="text-xs text-red-500 font-medium">❌ Yapılmadı</span>
                                                          )}
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </AccordionContent>
                                      </AccordionItem>
                                    );
                                  })}
                                </Accordion>
                              </CardContent>
                            </Card>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </TabsContent>
            )}

            {/* PARKLAR SEKMESİ */}
            {isEditing && (
              <TabsContent value="parks" className="space-y-4">
                {assignedParks.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
                    <TreePine className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg">Henüz sorumlu park atanmamış</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {assignedParks.map(park => (
                      <div key={park.id} className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg border border-green-200 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-lg">🌳</div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-green-800">{park.name}</h4>
                            {park.address && <p className="text-xs text-gray-600 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {park.address}</p>}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {park.total_area && <Badge variant="outline" className="text-xs bg-white">📐 {park.total_area} m²</Badge>}
                              {park.grass_area && <Badge variant="outline" className="text-xs bg-white">🌿 Çim: {park.grass_area} m²</Badge>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            {/* VEKALET SEKMESİ */}
            {isEditing && (
              <TabsContent value="substitutions" className="space-y-4">
                <SubstitutionTabContent 
                  personnelId={formData?.id} 
                  personnelName={formData?.name}
                  allPersonnel={personnel}
                />
              </TabsContent>
            )}
          </Tabs>
        </div>
    </DraggableModal>

    <Dialog
      open={setPasswordOpen}
      onOpenChange={(open) => {
        setSetPasswordOpen(open);
        if (!open) {
          setNewPassword("");
          setNewPasswordConfirm("");
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Şifre ata / değiştir</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">Bu personel için giriş şifresi (en az 4 karakter). İki alana aynı şifreyi yazın.</p>
        <Input
          type="password"
          placeholder="Yeni şifre"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          minLength={4}
          autoComplete="new-password"
          className="mt-2"
        />
        <Input
          type="password"
          placeholder="Yeni şifre (tekrar)"
          value={newPasswordConfirm}
          onChange={(e) => setNewPasswordConfirm(e.target.value)}
          minLength={4}
          autoComplete="new-password"
          className="mt-2"
        />
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => { setSetPasswordOpen(false); setNewPassword(""); setNewPasswordConfirm(""); }}>
            İptal
          </Button>
          <Button
            onClick={handleSetPassword}
            disabled={
              !newPassword.trim() ||
              newPassword.length < 4 ||
              newPassword !== newPasswordConfirm ||
              setPasswordLoading
            }
          >
            {setPasswordLoading ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Özel Rol Ata — yalnızca etiket, sistem rolleri etkilenmez */}
    <Dialog
      open={customRoleDialogOpen}
      onOpenChange={setCustomRoleDialogOpen}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Özel Rol Ata
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Bu rol sistem rolleri arasına eklenmez; yalnızca bu personelin
            kartında etiket olarak görüntülenir. Yetki ve hiyerarşi değişmez.
          </p>
          <div>
            <Label htmlFor="custom-role-name">Özel Rol Adı *</Label>
            <Input
              id="custom-role-name"
              value={customRoleDraft.name}
              onChange={(e) => setCustomRoleDraft((p) => ({ ...p, name: e.target.value }))}
              placeholder="Örn: Kent Tarımı Sorumlusu"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="custom-role-desc">Açıklama (opsiyonel)</Label>
            <Textarea
              id="custom-role-desc"
              value={customRoleDraft.description}
              onChange={(e) => setCustomRoleDraft((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              placeholder="Bu rolün kapsamını açıklayabilirsiniz..."
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => setCustomRoleDialogOpen(false)}>
            Vazgeç
          </Button>
          <Button
            onClick={() => {
              const name = customRoleDraft.name.trim();
              if (!name) {
                toast.error("Özel rol adı boş olamaz");
                return;
              }
              setFormData((prev) => ({
                ...prev,
                custom_role_name: name,
                custom_role_description: customRoleDraft.description.trim() || '',
              }));
              setCustomRoleDialogOpen(false);
            }}
          >
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog
      open={issueTempOpen}
      onOpenChange={(open) => {
        setIssueTempOpen(open);
        if (!open) setIssuedTemp(null);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Tek kullanımlık giriş kodu</DialogTitle>
        </DialogHeader>
        {issuedTemp && (
          <>
            <p className="text-sm text-gray-600">{issuedTemp.message}</p>
            <div className="my-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
              <div className="font-mono text-3xl font-bold tracking-widest text-amber-950">
                {issuedTemp.code}
              </div>
              {issuedTemp.expires_at && (
                <p className="mt-2 text-xs text-amber-900/80">
                  Son geçerlilik:{" "}
                  {new Date(issuedTemp.expires_at).toLocaleString("tr-TR", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(String(issuedTemp.code || ""));
                    toast.success("Kod panoya kopyalandı.");
                  } catch {
                    toast.error("Panoya kopyalanamadı.");
                  }
                }}
              >
                <Copy className="h-4 w-4" /> Panoya kopyala
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setIssueTempOpen(false);
                  setIssuedTemp(null);
                }}
              >
                Kapat
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>

    <AlertDialog
      open={loginBlockDialogOpen}
      onOpenChange={(open) => {
        setLoginBlockDialogOpen(open);
        if (!open) setLoginBlockPendingAction(null);
      }}
    >
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {loginBlockPendingAction === "block" ? "Girişi engelle" : "Giriş engelini kaldır"}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            {loginBlockPendingAction === "block" ? (
              <>
                <strong>{formData.name || "Bu kullanıcı"}</strong> giriş ekranında listelenmeyecek ve şifre ile giriş yapamayacak.
                Devam etmek istiyor musunuz?
              </>
            ) : (
              <>
                <strong>{formData.name || "Bu kullanıcı"}</strong> için giriş engelini kaldırmak üzeresiniz.
                Onaylıyor musunuz?
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loginBlockedLoading}>İptal</AlertDialogCancel>
          <Button
            type="button"
            disabled={loginBlockedLoading}
            className={loginBlockPendingAction === "block" ? "bg-orange-600 hover:bg-orange-700" : ""}
            onClick={() => executeLoginBlockChange()}
          >
            {loginBlockedLoading ? "Kaydediliyor..." : loginBlockPendingAction === "block" ? "Evet, engelle" : "Evet, kaldır"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

// Vekalet Sekmesi İçeriği Komponenti
const SubstitutionTabContent = ({ personnelId }) => {
  const [substitutionState, setSubstitutionState] = useState({ as_substitute: [], on_leave: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (personnelId) {
      fetchSubstitutionHistory();
    }
  }, [personnelId]);

  const fetchSubstitutionHistory = async () => {
    setLoading(true);
    try {
      // "İzin Yönetimi" ekranıyla birebir aynı veri kaynağı.
      const response = await axios.get(`${API}/api/active-substitutions/personnel/${personnelId}`);
      setSubstitutionState(response.data || { as_substitute: [], on_leave: [] });
    } catch (error) {
      console.error("Vekalet geçmişi yüklenirken hata:", error);
      setSubstitutionState({ as_substitute: [], on_leave: [] });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-600">Vekalet geçmişi yükleniyor...</span>
      </div>
    );
  }

  const asSubstitute = substitutionState.as_substitute || [];
  const onLeave = substitutionState.on_leave || [];
  const getParkNames = (sub) => {
    const parks = Array.isArray(sub?.parks) ? sub.parks : [];
    return parks
      .map((p) => (typeof p === 'string' ? p : (p?.name || p?.park_name || '')))
      .filter(Boolean);
  };

  return (
    <div className="space-y-3">
      <details className="group">
        <summary className="cursor-pointer list-none">
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-500 rounded-full p-2">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900">🤝 Vekalet Aldığım</h3>
                    <p className="text-xs text-amber-700">
                      {asSubstitute.length > 0 ? `${asSubstitute.length} kayıt` : 'Aktif vekalet yok'}
                    </p>
                  </div>
                </div>
                <ChevronDown className="h-5 w-5 text-amber-600 group-open:rotate-180 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </summary>
        <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          {asSubstitute.length === 0 ? (
            <p className="text-sm text-amber-800">Aktif vekalet yok.</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {asSubstitute.map((sub) => (
                <div key={sub.id} className="bg-white rounded border border-amber-200 p-2 text-sm">
                  <div className="font-semibold text-gray-800">{sub.original_personnel_name}</div>
                  <div className="text-xs text-gray-600">
                    {new Date(sub.start_date).toLocaleDateString('tr-TR')} - {new Date(sub.end_date).toLocaleDateString('tr-TR')}
                  </div>
                  {sub.parks?.length > 0 && (
                    <div className="text-[11px] text-gray-500 mt-1">
                      {sub.parks.length} park
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </details>

      <details className="group">
        <summary className="cursor-pointer list-none">
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <UserCheck className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-900">🧾 Vekalet Verdiğim</h3>
                    <p className="text-xs text-green-700">
                      {onLeave.length > 0 ? `${onLeave.length} kayıt` : 'Aktif vekalet yok'}
                    </p>
                  </div>
                </div>
                <ChevronDown className="h-5 w-5 text-green-600 group-open:rotate-180 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </summary>
        <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
          {onLeave.length === 0 ? (
            <p className="text-sm text-green-800">Aktif vekalet yok.</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {onLeave.map((sub) => (
                <div key={sub.id} className="bg-white rounded border border-green-200 p-2 text-sm">
                  <div className="font-semibold text-gray-800">{sub.substitute_personnel_name}</div>
                  <div className="text-xs text-gray-600">
                    {new Date(sub.start_date).toLocaleDateString('tr-TR')} - {new Date(sub.end_date).toLocaleDateString('tr-TR')}
                  </div>
                  {(sub.parks || []).length > 0 && (
                    <div className="text-[11px] text-green-700 mt-1 space-y-0.5">
                      {(sub.parks || []).map((p, idx) => {
                        const parkName = typeof p === 'string' ? p : (p?.name || p?.park_name || '');
                        const vekil = (p?.substitute_name || p?.substitute_personnel_name || sub?.substitute_personnel_name || '').trim();
                        return parkName ? <div key={idx}>🌳 {parkName} → {vekil || 'Vekil'}</div> : null;
                      })}
                    </div>
                  )}
                  {sub.reason_type && (
                    <div className="text-[11px] text-gray-500 mt-1">{sub.reason_type}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

export default PersonnelForm;
